import { useTranslation } from 'react-i18next';
import { sortLanguagesWithFrFirst } from '@/shared/utils/languageUtils';
import arrowIcon from '@/assets/svg/arrow_icon.svg';
import { LanguageTabs } from '../components/LanguageTabs';
import { ExhibitionFormFields } from '../components/ExhibitionFormFields';
import { LanguageModal } from '../components/LanguageModal';
import { ExhibitionReadView } from '../components/ExhibitionReadView';
import { CreateWorkModal } from '../components/CreateWorkModal';
import { ExhibitionWorksSection } from '../components/ExhibitionWorksSection';
import { AsyncError, AsyncSkeleton } from '@/shared/components/AsyncState';
import { ConfirmDialog } from '@/shared/components/ConfirmDialog';
import { useExhibitionDetail } from '../hooks/useExhibitionDetail';

export const ExhibitionDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const d = useExhibitionDetail();

  if (d.museumLoading || d.loading) {
    return <AsyncSkeleton variant="detail" />;
  }

  if (d.loadError || !d.exhibition) {
    return <AsyncError message={d.loadError || t('common.error')} onRetry={d.retryLoad} />;
  }

  return (
    <div className="create-exhibition-page">
      <button className="back-button" onClick={() => d.navigate(-1)}>
        <img src={arrowIcon} alt="" className="chevron-icon" />
        <span>{t('common.back')}</span>
      </button>

      <LanguageTabs
        activeLanguages={d.activeLanguages}
        currentLanguage={d.currentLanguage}
        onLanguageChange={(lang) => {
          d.setCurrentLanguage(lang);
          if (!d.translations[lang]) {
            d.setTranslations((prev) => ({
              ...prev,
              [lang]: { title: '', description: '' },
            }));
          }
        }}
        onAddLanguage={() => {
          const available = d.museumLanguages.filter((lang) => !d.activeLanguages.includes(lang));
          if (available.length > 0) {
            d.setSelectedLanguage(available[0]);
          }
          d.setLanguageModalOpen(true);
        }}
      />

      <LanguageModal
        isOpen={d.languageModalOpen}
        onClose={() => d.setLanguageModalOpen(false)}
        selectedLanguage={d.selectedLanguage}
        onLanguageSelect={d.setSelectedLanguage}
        activeLanguages={d.activeLanguages}
        museumLanguages={d.museumLanguages}
        onAddLanguage={(lang) => {
          if (!d.activeLanguages.includes(lang)) {
            const newLanguages = sortLanguagesWithFrFirst([...d.activeLanguages, lang]);
            d.setActiveLanguages(newLanguages);
            d.setCurrentLanguage(lang);
            if (!d.translations[lang]) {
              d.setTranslations({
                ...d.translations,
                [lang]: { title: '', description: '' },
              });
            }
            d.setIsEditing(true);
          }
          d.setLanguageModalOpen(false);
        }}
        languageDropdownOpen={d.languageDropdownOpen}
        onLanguageDropdownToggle={() => d.setLanguageDropdownOpen(!d.languageDropdownOpen)}
      />

      {!d.isEditing ? (
        <ExhibitionReadView
          exhibition={d.exhibition}
          currentLanguage={d.currentLanguage}
          imageUrl={d.imagePreview}
          onEdit={() => d.setIsEditing(true)}
        />
      ) : (
        <div className="exhibition-form-card">
          <ExhibitionFormFields
            formLanguage={d.currentLanguage}
            status={d.statusByLanguage[d.currentLanguage] || 'draft'}
            onStatusChange={(newStatus) => {
              d.setStatusByLanguage({
                ...d.statusByLanguage,
                [d.currentLanguage]: newStatus,
              });
            }}
            color={d.color}
            onColorChange={d.setColor}
            order={d.order}
            onOrderChange={async (newOrder) => {
              if (d.exhibitionId && d.allExhibitions.length > 0) {
                await d.changeExhibitionOrder(d.allExhibitions, d.exhibitionId, newOrder);
              } else {
                d.setOrder(newOrder);
              }
            }}
            totalCount={d.allExhibitions.length || 1}
            selectedImage={d.selectedImage}
            imagePreview={d.imagePreview}
            onImageSelect={d.handleImageSelect}
            onImageDelete={d.handleImageDelete}
            title={d.translations[d.currentLanguage]?.title || ''}
            onTitleChange={(title) => {
              d.setTranslations({
                ...d.translations,
                [d.currentLanguage]: {
                  ...d.translations[d.currentLanguage],
                  title,
                },
              });
            }}
            description={d.translations[d.currentLanguage]?.description || ''}
            onDescriptionChange={(description) => {
              d.setTranslations({
                ...d.translations,
                [d.currentLanguage]: {
                  ...d.translations[d.currentLanguage],
                  description,
                },
              });
            }}
            coverAlt={d.translations[d.currentLanguage]?.coverAlt || ''}
            onCoverAltChange={(coverAlt) => {
              d.setTranslations({
                ...d.translations,
                [d.currentLanguage]: { ...d.translations[d.currentLanguage], coverAlt },
              });
            }}
            statusDropdownOpen={d.statusDropdownOpen}
            onStatusDropdownToggle={() => {
              d.setStatusDropdownOpen(!d.statusDropdownOpen);
              d.setColorDropdownOpen(false);
              d.setOrderDropdownOpen(false);
            }}
            colorDropdownOpen={d.colorDropdownOpen}
            onColorDropdownToggle={() => {
              d.setColorDropdownOpen(!d.colorDropdownOpen);
              d.setStatusDropdownOpen(false);
              d.setOrderDropdownOpen(false);
            }}
            orderDropdownOpen={d.orderDropdownOpen}
            onOrderDropdownToggle={() => {
              d.setOrderDropdownOpen(!d.orderDropdownOpen);
              d.setStatusDropdownOpen(false);
              d.setColorDropdownOpen(false);
            }}
            colorMap={d.exhibitionColorMap}
            availableColors={d.effectiveAvailableColors}
            onSave={d.handleSaveDraft}
            onCancel={d.handleCancel}
            saving={d.saving}
            saveLabel={
              d.statusByLanguage[d.currentLanguage] === 'published'
                ? t('common.save')
                : t('exhibitions.saveDraft')
            }
            onDelete={() => d.setShowDeleteConfirmation(true)}
          />
        </div>
      )}

      <ExhibitionWorksSection
        works={d.works}
        filteredWorks={d.filteredWorks}
        loading={d.loadingWorks}
        error={d.worksError}
        currentLanguage={d.currentLanguage}
        episodeFilter={d.episodeFilter}
        draggedWorkId={d.draggedWorkId}
        dragOverWorkId={d.dragOverWorkId}
        dragOverPlace={d.dragOverPlace}
        onFilterChange={d.setEpisodeFilter}
        onCreate={() => d.setCreateWorkModalOpen(true)}
        onRetry={d.refetchWorks}
        onEdit={d.handleEditWork}
        onDragStart={d.handleDragStart}
        onDragOver={d.handleDragOver}
        onDragLeave={d.handleDragLeave}
        onDrop={d.handleDrop}
        onDragEnd={d.handleDragEnd}
      />

      {d.museum?.id && d.exhibitionId && (
        <CreateWorkModal
          isOpen={d.createWorkModalOpen}
          onClose={d.handleCloseWorkModal}
          onSuccess={d.handleWorkCreated}
          museumId={d.museum.id}
          exhibitionId={d.exhibitionId}
          exhibitionActiveLanguages={d.activeLanguages}
          exhibitionCurrentLanguage={d.currentLanguage}
          onExhibitionLanguageChange={d.setCurrentLanguage}
          exhibitionColor={d.exhibition?.color ?? d.color}
          work={d.editingWork}
        />
      )}

      <ConfirmDialog
        isOpen={d.showDeleteConfirmation}
        title={t('exhibitions.confirmDelete')}
        confirmLabel={d.deleting ? t('common.loading') : t('exhibitions.deleteExhibition')}
        cancelLabel={t('common.cancel')}
        onConfirm={d.handleConfirmDelete}
        onCancel={() => d.setShowDeleteConfirmation(false)}
        confirming={d.deleting}
      />
    </div>
  );
};
